import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode } from "vue"

import { computed, inject, ref, watch } from 'vue';
import { useFieldArray } from 'vee-validate';
import OcrVerificationProductsTableRow from 'vcomponents/ocr/OcrVerificationProductsTableRow.vue';
import Item from 'api/item';
import type { OcrProduct } from 'composables/ocr/ocrFormSchema';
import { setProductDescriptionFromAccountingCode } from 'composables/useOcrProducts';
import type { AccountingCode } from 'types/entities/accounting/accountingCode.type';
import type { TaxRate } from 'types/entities/accounting/taxRate.type';
import type { Unit } from 'types/entities/units/unit.type';
import type { Warehouse } from 'types/entities/stock/warehouse.type';
import type { PushToast } from 'types/injects/pushToast.type';
import type { Translator } from 'types/injects/translator.type';

interface Props {
    units?: Unit[];
    warehouses?: Warehouse[];
    isStockEnabled?: boolean;
    isLineItemsEnabled?: boolean;
    hint: string;
    accountingCodes: AccountingCode[];
    taxRates: TaxRate[];
    showAnalyticCode?: boolean;
    currencySymbol?: string;
    formatDateDisplayed?: string;
    addProduct: (product?: OcrProduct) => void;
    requestProductDelete: (index: number) => void;
    disabled?: boolean;
    isElectronic?: boolean;
    decimals: number;
}

interface ItemResponse {
    accounting_purchase_code_id: number | null;
    type: 'service' | 'item' | string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'OcrVerificationProductsTable',
  props: {
    units: { default: () => [] },
    warehouses: { default: () => [] },
    isStockEnabled: { type: Boolean, default: false },
    isLineItemsEnabled: { type: Boolean, default: false },
    hint: {},
    accountingCodes: {},
    taxRates: {},
    showAnalyticCode: { type: Boolean, default: false },
    currencySymbol: { default: undefined },
    formatDateDisplayed: { default: undefined },
    addProduct: {},
    requestProductDelete: {},
    disabled: { type: Boolean, default: false },
    isElectronic: { type: Boolean, default: false },
    decimals: {}
  },
  setup(__props: any) {

const props = __props;

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const productsArray = useFieldArray<OcrProduct>('products');

const hasCatalogueLink = (product: OcrProduct): boolean => Boolean(product.catalogueLink);

const firstField = productsArray.fields.value[0];
const expandedIndex = ref<number | null>(
    firstField && hasCatalogueLink(firstField.value) ? 0 : null,
);

const canDelete = computed<boolean>(() => productsArray.fields.value.length > 1);
const showAnalyticCodeColumn = computed<boolean>(
    () => Boolean(props.showAnalyticCode) && Boolean(props.isLineItemsEnabled),
);

watch(
    () => productsArray.fields.value.length,
    (length, oldLength = 0) => {
        if (length > oldLength) {
            const firstField = productsArray.fields.value[0];
            if (oldLength === 0 && length > 0 && firstField && hasCatalogueLink(firstField.value)) {
                expandedIndex.value = 0;
            }
        } else {
            if (expandedIndex.value !== null && expandedIndex.value >= length) {
                expandedIndex.value = null;
            }
        }
    },
);

const handleAccountingCodeUpdate = (value: number | null, index: number): void => {
    const current = productsArray.fields.value[index]?.value;
    if (!current) {
        return;
    }
    const next: OcrProduct = { ...current, accountingCode: value };
    setProductDescriptionFromAccountingCode(next, props.accountingCodes);
    productsArray.update(index, next);
};

const handleCatalogueLinkUpdate = async (
    value: OcrProduct['catalogueLink'] | '',
    index: number,
): Promise<void> => {
    const current = productsArray.fields.value[index]?.value;
    if (!current) {
        return;
    }
    const next: OcrProduct = { ...current, catalogueLink: value === '' ? null : value };

    if (
        next.catalogueLink &&
        next.catalogueLink.id === undefined &&
        next.catalogueLink.value !== undefined
    ) {
        const numValue = Number(next.catalogueLink.value);
        next.catalogueLink.id = Number.isNaN(numValue) ? undefined : numValue;
    }

    const itemId = next.catalogueLink?.id;
    if (itemId) {
        try {
            const { data } = (await Item.getItemById(itemId)) as { data: ItemResponse };
            if (data.accounting_purchase_code_id) {
                next.accountingCode = data.accounting_purchase_code_id;
            }
            if (next.catalogueLink) {
                next.catalogueLink.itemType = data.type === 'service' ? 'service' : 'item';
            }
        } catch {
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                icon: true,
                text: $t.__('ocr.OcrVerificationForm.error_generic'),
                duration: 5000,
            });
        }
    }
    productsArray.update(index, next);
};

const handleToggleExpand = (index: number, expanded?: boolean): void => {
    const product = productsArray.fields.value[index]?.value;
    if (!product || !hasCatalogueLink(product)) {
        return;
    }
    const shouldExpand = typeof expanded === 'boolean' ? expanded : expandedIndex.value !== index;
    expandedIndex.value = shouldExpand ? index : null;
};

const handleProductUpdated = (
    itemId: number,
    updatedName: string,
    accountingPurchaseCodeId: number | null,
): void => {
    for (const [index, field] of productsArray.fields.value.entries()) {
        if (field.value.catalogueLink?.id === itemId) {
            const next: OcrProduct = {
                ...field.value,
                catalogueLink: { ...field.value.catalogueLink, label: updatedName },
            };
            if (accountingPurchaseCodeId) {
                next.accountingCode = accountingPurchaseCodeId;
            }
            productsArray.update(index, next);
        }
    }
};

const handleAddProduct = (): void => {
    props.addProduct();
};

const handleRequestProductDelete = (index: number): void => {
    props.requestProductDelete(index);
};

const handleRequestDuplicate = (index: number): void => {
    const current = productsArray.fields.value[index]?.value;
    if (current) {
        props.addProduct(current);
    }
};

return (_ctx: any,_cache: any) => {
  const _component_CompassAlert = _resolveComponent("CompassAlert")!
  const _component_CompassTableHeadCell = _resolveComponent("CompassTableHeadCell")!
  const _component_CompassTableHead = _resolveComponent("CompassTableHead")!
  const _component_CompassTableBody = _resolveComponent("CompassTableBody")!
  const _component_CompassTable = _resolveComponent("CompassTable")!
  const _component_CompassButton = _resolveComponent("CompassButton")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_component_CompassAlert, {
      type: _ctx.CompassEnums.AlertType.INFORMATIVE,
      description: __props.hint
    }, null, 8 /* PROPS */, ["type", "description"]),
    _createElementVNode("div", null, [
      _createVNode(_component_CompassTable, {
        size: _ctx.CompassEnums.TableSize.MEDIUM,
        hasExpandableRows: props.isStockEnabled
      }, {
        default: _withCtx(() => [
          _createVNode(_component_CompassTableHead, null, {
            default: _withCtx(() => [
              _createVNode(_component_CompassTableHeadCell, null, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref($t).__('ocr.OcrVerificationProductsTable.supplier_product')), 1 /* TEXT */)
                ]),
                _: 1 /* STABLE */
              }),
              (props.isLineItemsEnabled)
                ? (_openBlock(), _createBlock(_component_CompassTableHeadCell, {
                    key: 0,
                    class: "ocr-verification-products-table__header--align-right"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref($t).__('common.labels.quantity_shorten')), 1 /* TEXT */)
                    ]),
                    _: 1 /* STABLE */
                  }))
                : _createCommentVNode("v-if", true),
              _createVNode(_component_CompassTableHeadCell, { class: "ocr-verification-products-table__header--align-right" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref($t).__('common.labels.cost_excluding_tax')), 1 /* TEXT */)
                ]),
                _: 1 /* STABLE */
              }),
              _createVNode(_component_CompassTableHeadCell, null, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref($t).__('ocr.OcrVerificationForm.tax_rate')), 1 /* TEXT */)
                ]),
                _: 1 /* STABLE */
              }),
              (props.isLineItemsEnabled)
                ? (_openBlock(), _createBlock(_component_CompassTableHeadCell, { key: 1 }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref($t).__('ocr.OcrVerificationProductsTable.unit_optional')), 1 /* TEXT */)
                    ]),
                    _: 1 /* STABLE */
                  }))
                : _createCommentVNode("v-if", true),
              _createVNode(_component_CompassTableHeadCell, { class: "ocr-verification-products-table__header--align-right" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref($t).__('ocr.OcrVerificationProductsTable.tax_amount')), 1 /* TEXT */)
                ]),
                _: 1 /* STABLE */
              }),
              _createVNode(_component_CompassTableHeadCell, { class: "ocr-verification-products-table__header--align-right" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref($t).__('ocr.OcrVerificationProductsTable.amount_incl_tax')), 1 /* TEXT */)
                ]),
                _: 1 /* STABLE */
              }),
              (props.isLineItemsEnabled)
                ? (_openBlock(), _createBlock(_component_CompassTableHeadCell, { key: 2 }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref($t).__('ocr.OcrVerificationProductsTable.catalogue_link_optional')), 1 /* TEXT */)
                    ]),
                    _: 1 /* STABLE */
                  }))
                : _createCommentVNode("v-if", true),
              _createVNode(_component_CompassTableHeadCell, null, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref($t).__('common.labels.accounting_code')), 1 /* TEXT */)
                ]),
                _: 1 /* STABLE */
              }),
              (showAnalyticCodeColumn.value)
                ? (_openBlock(), _createBlock(_component_CompassTableHeadCell, { key: 3 }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref($t).__('ocr.OcrVerificationProductsTable.analytic_code_optional')), 1 /* TEXT */)
                    ]),
                    _: 1 /* STABLE */
                  }))
                : _createCommentVNode("v-if", true),
              _createVNode(_component_CompassTableHeadCell, {
                id: "delete",
                fixed: "right",
                defaultWidth: 80
              })
            ]),
            _: 1 /* STABLE */
          }),
          _createVNode(_component_CompassTableBody, null, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(productsArray).fields.value, (field, index) => {
                return (_openBlock(), _createBlock(OcrVerificationProductsTableRow, {
                  key: field.key,
                  disabled: props.disabled || props.isElectronic,
                  index: index,
                  canExpand: props.isStockEnabled && hasCatalogueLink(field.value),
                  isExpanded: expandedIndex.value === index,
                  canDelete: canDelete.value,
                  taxRates: props.taxRates,
                  accountingCodes: props.accountingCodes,
                  units: props.units,
                  warehouses: props.warehouses,
                  showAnalyticCode: props.showAnalyticCode,
                  isLineItemsEnabled: props.isLineItemsEnabled,
                  currencySymbol: props.currencySymbol,
                  formatDateDisplayed: props.formatDateDisplayed,
                  decimals: props.decimals,
                  onAccountingCodeUpdate: _cache[0] || (_cache[0] = 
                            (value, idx) => handleAccountingCodeUpdate(value, idx)
                        ),
                  onCatalogueLinkUpdate: _cache[1] || (_cache[1] = 
                            (value, idx) => handleCatalogueLinkUpdate(value, idx)
                        ),
                  onToggleExpand: _cache[2] || (_cache[2] = (idx, expanded) => handleToggleExpand(idx, expanded)),
                  onRequestDelete: handleRequestProductDelete,
                  onRequestDuplicate: handleRequestDuplicate,
                  onProductUpdated: handleProductUpdated
                }, null, 8 /* PROPS */, ["disabled", "index", "canExpand", "isExpanded", "canDelete", "taxRates", "accountingCodes", "units", "warehouses", "showAnalyticCode", "isLineItemsEnabled", "currencySymbol", "formatDateDisplayed", "decimals"]))
              }), 128 /* KEYED_FRAGMENT */))
            ]),
            _: 1 /* STABLE */
          })
        ]),
        _: 1 /* STABLE */
      }, 8 /* PROPS */, ["size", "hasExpandableRows"]),
      _createVNode(_component_CompassButton, {
        class: "ocr-verification-products-table__add-line",
        label: _unref($t).__('ocr.OcrVerificationForm.add_line'),
        type: _ctx.CompassEnums.ButtonType.TERTIARY,
        disabled: props.disabled || props.isElectronic,
        leftIcon: "plus",
        onClick: handleAddProduct
      }, null, 8 /* PROPS */, ["label", "type", "disabled"])
    ])
  ]))
}
}

})